/* Created by Interactive 63 - https://interactive63.com */
/**
 * Normalises path data into cubic Bézier subpaths, the common form that every morph works in.
 * Lines and quadratics become cubics, arcs are approximated by cubics, and an open end that
 * should close gains an explicit closing line. Parsing uses svgpath and so lives in the editor
 * side of the code only; the runtime receives plain number arrays.
 */
import svgpath from 'svgpath';

/** Control point one, control point two, then the end point. */
export type Cubic = [c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number];

export interface Subpath {
  start: [x: number, y: number];
  segs: Cubic[];
  closed: boolean;
}

export type NormaliseResult = { ok: true; subpaths: Subpath[] } | { ok: false; message: string };

const UNREADABLE = "This path data couldn't be read.";
const EPSILON = 1e-9;

/** A straight line as a cubic, with control points a third and two thirds along it. */
function lineCubic(x0: number, y0: number, x1: number, y1: number): Cubic {
  return [
    x0 + (x1 - x0) / 3,
    y0 + (y1 - y0) / 3,
    x0 + ((x1 - x0) * 2) / 3,
    y0 + ((y1 - y0) * 2) / 3,
    x1,
    y1,
  ];
}

/** Converts path data into cubic subpaths, or explains why it could not be read. */
export function toCubicSubpaths(d: string): NormaliseResult {
  if (d.trim() === '') return { ok: false, message: UNREADABLE };
  const subpaths: Subpath[] = [];
  let current: Subpath | null = null;
  let x = 0;
  let y = 0;
  const state = { valid: true };

  /** Starts a subpath at a point. */
  const begin = (px: number, py: number): void => {
    current = { start: [px, py], segs: [], closed: false };
    subpaths.push(current);
    x = px;
    y = py;
  };
  /** Adds a cubic to the current subpath, starting one if needed. */
  const add = (c: Cubic): void => {
    if (!current) begin(x, y);
    current?.segs.push(c);
    x = c[4];
    y = c[5];
  };

  try {
    svgpath(d)
      .abs()
      .unshort()
      .unarc()
      .iterate((segment) => {
        const command = segment[0];
        const args = segment.slice(1) as number[];
        if (args.some((value) => !Number.isFinite(value))) state.valid = false;
        switch (command) {
          case 'M':
            begin(args[0] as number, args[1] as number);
            break;
          case 'L':
            add(lineCubic(x, y, args[0] as number, args[1] as number));
            break;
          case 'H':
            add(lineCubic(x, y, args[0] as number, y));
            break;
          case 'V':
            add(lineCubic(x, y, x, args[0] as number));
            break;
          case 'C':
            add(args as Cubic);
            break;
          case 'Q': {
            const [qx, qy, px, py] = args as [number, number, number, number];
            add([
              x + (2 / 3) * (qx - x),
              y + (2 / 3) * (qy - y),
              px + (2 / 3) * (qx - px),
              py + (2 / 3) * (qy - py),
              px,
              py,
            ]);
            break;
          }
          case 'Z': {
            const open = current;
            if (!open) break;
            if (Math.hypot(x - open.start[0], y - open.start[1]) > EPSILON) {
              add(lineCubic(x, y, open.start[0], open.start[1]));
            }
            open.closed = true;
            x = open.start[0];
            y = open.start[1];
            // A command after Z continues from the start point in a new subpath.
            current = null;
            break;
          }
          default:
            state.valid = false;
        }
      });
  } catch {
    return { ok: false, message: UNREADABLE };
  }

  const usable = subpaths.filter((s) => s.segs.length > 0);
  if (!state.valid || usable.length === 0) return { ok: false, message: UNREADABLE };
  return { ok: true, subpaths: usable };
}
